'use client';

import { useRouter } from '@/i18n/routing';
import { Link } from '@/i18n/routing';
import { useContext, useEffect, useRef, useState } from 'react';
import SampleButton from './sample-button';
import { ReportContext } from '@/app/providers/reportProvider';
import { useLocale, useTranslations } from 'next-intl';
import { removeSpacesFromKeyword } from '@/app/utils/helpers';

export default function ScrollSpy() {
  const navRefs = useRef([]);
  const sectionRefs = useRef([]);
  const [isTocActive, setIsTocActive] = useState(false);
  const router = useRouter();
  const report = useContext(ReportContext);
  const regionalText = useTranslations('scrollspy');
  const locale = useLocale();

  //Sections of the Page
  const sections = [
    {
      id: 'about-report',
      title: regionalText('aboutreport'),
      className:
        'uppercase heading font-semibold my-1 py-1 px-2 rounded-md  nav-link about-report',
    },
    {
      id: 'about-report-2',
      title: regionalText('marketoverview'),
      className: `my-1 nav-link ms-3 py-1 px-2 mx-2 rounded-md ${
        isTocActive ? 'close' : 'open'
      }`,
    },
    {
      id: 'about-report-1',
      title: regionalText('marketsnapshot'),
      className: `my-1 nav-link ms-3 py-1 px-2 mx-2 rounded-md ${
        isTocActive ? 'close' : 'open'
      }`,
    },

    {
      id: 'about-report-3',
      title: regionalText('markettrends'),
      className: `my-1 nav-link ms-3 py-1 px-2 mx-2 rounded-md ${
        isTocActive ? 'close' : 'open'
      }`,
    },
    {
      id: 'about-report-9',
      title: regionalText('prepreferences'),
      className: `my-1 nav-link ms-3 py-1 px-2 mx-2 rounded-md ${
        isTocActive ? 'close' : 'open'
      }`,
    },
    {
      id: 'about-report-11',
      title: regionalText('optionanalysis'),
      className: `my-1 nav-link ms-3 py-1 px-2 mx-2 rounded-md ${
        isTocActive ? 'close' : 'open'
      }`,
    },
    {
      id: 'about-report-10',
      title: regionalText('winningstratagies'),
      className: `my-1 nav-link ms-3 py-1 px-2 mx-2 rounded-md w-[85%] ${
        isTocActive ? 'close' : 'open'
      }`,
    },
    {
      id: 'about-report-7',
      title: regionalText('segmentalanalysis'),
      className: `my-1 nav-link ms-3 py-1 px-2 mx-2 rounded-md ${
        isTocActive ? 'close' : 'open'
      }`,
    },
    {
      id: 'about-report-8',
      title: regionalText('additionalinsights'),
      className: `my-1 nav-link ms-3 py-1 px-2 mx-2 rounded-md ${
        isTocActive ? 'close' : 'open'
      }`,
    },
    {
      id: 'about-report-4',
      title: regionalText('competitivemarket'),
      className: `my-1 nav-link ms-3 py-1 px-2 mx-2 rounded-md ${
        isTocActive ? 'close' : 'open'
      }`,
    },
    {
      id: 'about-report-5',
      title: regionalText('majorplayers'),
      className: `my-1 nav-link ms-3 py-1 px-2 mx-2 rounded-md ${
        isTocActive ? 'close' : 'open'
      }`,
    },
    {
      id: 'about-report-6',
      title: regionalText('recentDevelopments'),
      className: `my-1 nav-link ms-3 py-1 px-2 mx-2 rounded-md ${
        isTocActive ? 'close' : 'open'
      }`,
    },
    {
      id: 'TOC-nav',
      title: regionalText('toc'),
      className:
        'uppercase heading font-semibold nav-btn-act my-1 rounded-md  nav-link py-1 px-2',
    },
    {
      id: 'SOR',
      title: regionalText('sor'),
      className:
        'uppercase heading font-semibold nav-btn-act my-1 rounded-md  nav-link py-1 px-2',
    },
    {
      id: 'FAQ',
      title: regionalText('faq'),
      className:
        'uppercase heading font-semibold nav-btn-act my-1 rounded-md  nav-link py-1 px-2',
    },
  ];

  const handleClick = (e, targetId) => {
    e.preventDefault();
    const targetSection = document.getElementById(targetId);
    if (targetSection) {
      window.scrollTo({
        top: targetSection.offsetTop - 150,
        behavior: 'smooth',
      });
    } else {
      let slug = report.CustomName;
      if (locale === 'ja' || locale === 'ko') {
        slug = removeSpacesFromKeyword(report.keyword) + '-' + report.newsId;
      }
      router.push(`/industry-reports/${slug}`);
    }
  };

  //ScrollSpy Effects
  useEffect(() => {
    //Getting the Each Section and pushing it to  the sectionRefs
    for (const section of sections) {
      let sectionRef = document.getElementById(section.id);
      if (sectionRef !== null) {
        sectionRefs.current.push(sectionRef);
      }
    }

    const handleScroll = () => {
      let currentSection = sectionRefs.current[0];

      for (const section of sectionRefs.current) {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 200) {
          currentSection = section;
        }
      }

      if (navRefs.current.length !== 0) {
        try {
          navRefs.current.forEach((link) => {
            if ((link && link.dataset.target) === currentSection.id) {
              link.classList.add('active');
            } else {
              link.classList.remove('active');
            }

            if (
              currentSection.id === 'TOC-nav' ||
              currentSection.id === 'SOR' ||
              currentSection.id === 'FAQ' ||
              currentSection.id === 'about-report'
            ) {
              setIsTocActive(true);
            } else {
              setIsTocActive(false);
            }
          });
        } catch (err) {
          console.log(err);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });

  return (
    <nav className="nav nav-pills flex flex-col">
      {sections.map((section, index) => {
        if (
          (section.title === 'Segmental Analysis' ||
            section.title === regionalText('segmentalanalysis')) &&
          !report.segmental_analysis
        ) {
          return;
        }
        if (
          (section.title === 'Recent Developements' ||
            section.title === regionalText('recentDevelopments')) &&
          !report.recentdevelopment
        ) {
          return;
        }
        if (
          (section.title === 'Additional Insights' ||
            section.title === regionalText('additionalinsights')) &&
          !report.additionalinsights
        ) {
          return;
        }
        if (
          (section.title === 'Prescribers Preferences' ||
            section.title === regionalText('prepreferences')) &&
          !report.prepreferences
        ) {
          return;
        }
        if (
          (section.title === 'Key winning strategies adopted by key players' ||
            section.title === regionalText('winningstratagies')) &&
          !report.winningstratagies
        ) {
          return;
        }
        if (
          (section.title === 'Treatment option analysis' ||
            section.title === regionalText('optionanalysis')) &&
          !report.optionanalysis
        ) {
          return;
        }

        return (
          <Link
            className={section.className}
            key={section.id}
            href={`#${section.id}`}
            onClick={(e) => handleClick(e, section.id)}
            data-target={section.id}
            ref={(el) => (navRefs.current[index] = el)}
          >
            {section.title}
          </Link>
        );
      })}
      <SampleButton
        className="btn btn-dowload-now mt-3 rounded-xl transition duration-300 hover:scale-105 w-full"
        keyword={report.keyword}
        CustomName={report.CustomName}
        reportId={report.newsId}
        forecastPeriod={report.studyperiod}
      >
        {regionalText('sample_text')}
      </SampleButton>
    </nav>
  );
}
